import { useMemo } from 'react'
import { Trans, useLingui } from '@lingui/react/macro'
import { GraphQL } from '@guestgoodie/graphql'

import Button from '@/components/ui/Button'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import { useSync } from '../hooks/useSync'
import { useFeatures } from '../hooks/useFeatures'

export function SyncButton({ type }: { type: GraphQL.SyncableObject }) {
  const { t } = useLingui()
  const { sync, loading } = useSync(type)
  const features = useFeatures()

  const typeTranslation = useMemo(() => {
    switch (type) {
      case GraphQL.SyncableObject.Rooms:
        return t`Re-Sync Rooms with PMS`
      case GraphQL.SyncableObject.Stays:
        return t`Re-Sync Stays with PMS`
      case GraphQL.SyncableObject.Bookings:
        return t`Re-Sync Orders with Booking Engines`
      case GraphQL.SyncableObject.BookableServices:
        return t`Re-Sync Bookable Services with Booking Engines`
      case GraphQL.SyncableObject.Doors:
        return t`Re-Sync Doors with Door Systems`
    }
  }, [t, type])

  const isSyncing = useMemo(() => {
    return features?.some(f => f.syncing?.includes(type)) ?? false
  }, [features, type])

  const shouldShow = useMemo(() => {
    return features?.some(feature => {
      switch (feature.type) {
        case GraphQL.IntegrationTypes.Pms:
          return [
            GraphQL.SyncableObject.Rooms,
            GraphQL.SyncableObject.Stays
          ].includes(type)

        case GraphQL.IntegrationTypes.BookingEngine:
          return [
            GraphQL.SyncableObject.BookableServices,
            GraphQL.SyncableObject.Bookings
          ].includes(type)

        case GraphQL.IntegrationTypes.DoorSystem:
          return [GraphQL.SyncableObject.Doors].includes(type)
      }
    })
  }, [features, type])

  if (!shouldShow) return null
  if (!typeTranslation) return null

  return (
    <ConfirmDialog
      asChild
      title={typeTranslation}
      message={t`This should barely ever be needed, only do this if you are missing data or their is data that should have been deleted.`}
      onConfirm={sync}
    >
      <Button variant="outline" loading={loading || isSyncing}>
        <Trans>Re-Sync</Trans>
      </Button>
    </ConfirmDialog>
  )
}
